import { useCallback, useEffect, useRef, useState } from 'react'
import { Link, useSearchParams } from 'react-router'
import { templatesApiService } from '../services/templatesApiService'
import { ApiError } from '../services/BaseApiService'
import { Badge, Button, PageHeader } from '../components/ui'
import { APPROVAL_LEVEL_NAME, useRequestDecision } from '../components/templates/PromotionApproval'
import { useCurrentUser } from '../hooks/useCurrentUser'
import { colors, fontSize, radius, shadow, spacing } from '../styles/theme'
import type { PromotionRequest, PromotionRequestStatus } from '../types'

type Tab = 'pending' | 'history'

const STATUS_STYLE: Record<PromotionRequestStatus, { color: string; bg: string; border: string }> = {
  pending:    { color: colors.warning, bg: colors.warningLight, border: colors.warningBorder },
  approved:   { color: colors.success, bg: colors.successLight, border: colors.successBorder },
  rejected:   { color: colors.error, bg: colors.errorLight, border: colors.errorBorder },
  cancelled:  { color: colors.textSecondary, bg: colors.background, border: colors.border },
  superseded: { color: colors.textSecondary, bg: colors.background, border: colors.border },
  expired:    { color: colors.warning, bg: colors.warningLight, border: colors.warningBorder },
}

const STATUS_LABEL: Record<PromotionRequestStatus, string> = {
  pending: 'Pending', approved: 'Approved', rejected: 'Rejected', cancelled: 'Cancelled', superseded: 'Superseded', expired: 'Expired',
}

const fmt = (iso: string | null) => (iso ? new Date(iso).toLocaleString() : '—')

export default function PromotionApprovalsPage() {
  const me = useCurrentUser()
  // ?request= comes from the approval email: highlight that row and offer to approve it once.
  const [searchParams] = useSearchParams()
  const focusId = searchParams.get('request')
  const focused = useRef(false)
  const [tab, setTab] = useState<Tab>('pending')
  const [rows, setRows] = useState<PromotionRequest[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')

  const load = useCallback(async (which: Tab) => {
    setLoading(true)
    setError('')
    try {
      const list = which === 'pending'
        ? await templatesApiService.listPromotionRequests({ status: 'pending' })
        : (await templatesApiService.listPromotionRequests()).filter(r => r.status !== 'pending')
      setRows(list)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to load approval requests')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { load(tab) }, [tab, load])

  const { open, modal } = useRequestDecision(message => { setNotice(message); load(tab) })

  useEffect(() => {
    if (!focusId || loading || focused.current) return
    const row = rows.find(r => r.promotionRequestId === focusId)
    if (!row) return
    focused.current = true
    document.querySelector('tr[aria-current="true"]')?.scrollIntoView?.({ block: 'center' })
    if (row.status === 'pending' && row.canApprove) open('approve', row)
  }, [focusId, loading, rows, open])

  const th: React.CSSProperties = {
    padding: `${spacing.sm} ${spacing.md}`, textAlign: 'left', fontSize: fontSize.sm, fontWeight: 600,
    color: colors.textSecondary, borderBottom: `1px solid ${colors.border}`, background: colors.background, whiteSpace: 'nowrap',
  }
  const td: React.CSSProperties = {
    padding: `${spacing.sm} ${spacing.md}`, fontSize: fontSize.sm, color: colors.text, borderBottom: `1px solid ${colors.border}`, verticalAlign: 'top',
  }

  function actions(r: PromotionRequest) {
    if (me && r.requestedBy === me.userId && r.canApprove) {
      return (
        <div style={{ display: 'flex', gap: spacing.xs, justifyContent: 'flex-end' }}>
          <Button size="sm" onClick={() => open('approve', r)}>Approve</Button>
          <Button size="sm" variant="ghost" onClick={() => open('cancel', r)}>Cancel</Button>
        </div>
      )
    }
    if (r.canApprove) {
      return (
        <div style={{ display: 'flex', gap: spacing.xs, justifyContent: 'flex-end' }}>
          <Button size="sm" onClick={() => open('approve', r)}>Approve</Button>
          <Button size="sm" variant="danger" onClick={() => open('reject', r)}>Reject</Button>
        </div>
      )
    }
    if (me && r.requestedBy === me.userId) {
      return (
        <div style={{ display: 'flex', gap: spacing.xs, alignItems: 'center', justifyContent: 'flex-end' }}>
          <span style={{ color: colors.textSecondary }}>Your request</span>
          <Button size="sm" variant="ghost" onClick={() => open('cancel', r)}>Cancel</Button>
        </div>
      )
    }
    return <span style={{ color: colors.textSecondary }}>Awaiting another approver</span>
  }

  return (
    <div>
      <PageHeader title="Approvals" subtitle="Requests to promote template versions to Live." />

      <div style={{ display: 'flex', gap: 4, marginBottom: spacing.lg }}>
        {(['pending', 'history'] as Tab[]).map(t => (
          <button key={t} onClick={() => { setNotice(''); setTab(t) }} aria-pressed={tab === t}
            style={{
              padding: '5px 12px', borderRadius: radius.full, fontSize: fontSize.sm, cursor: 'pointer',
              border: `1px solid ${tab === t ? colors.primary : colors.border}`,
              background: tab === t ? colors.primary : colors.surface,
              color: tab === t ? colors.onPrimary : colors.textSecondary,
              fontWeight: tab === t ? 600 : 400,
            }}>
            {t === 'pending' ? 'Pending' : 'History'}
          </button>
        ))}
      </div>

      {error && (
        <div style={{ background: colors.errorLight, color: colors.error, border: `1px solid ${colors.errorBorder}`, borderRadius: radius.sm, padding: `${spacing.sm} ${spacing.md}`, fontSize: fontSize.sm, marginBottom: spacing.md }}>
          {error}
        </div>
      )}
      {notice && (
        <div style={{ background: colors.successLight, color: colors.success, border: `1px solid ${colors.successBorder}`, borderRadius: radius.sm, padding: `${spacing.sm} ${spacing.md}`, fontSize: fontSize.sm, marginBottom: spacing.md }}>
          {notice}
        </div>
      )}

      {loading ? (
        <div style={{ color: colors.textSecondary }}>Loading...</div>
      ) : rows.length === 0 ? (
        <div style={{ background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: radius.md, padding: spacing.xxl, textAlign: 'center', color: colors.textSecondary, boxShadow: shadow.sm }}>
          {tab === 'pending' ? 'No requests are waiting for approval.' : 'No decided requests yet.'}
        </div>
      ) : (
        <div style={{ background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: radius.md, boxShadow: shadow.sm, overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={th}>Template</th>
                <th style={th}>Version</th>
                <th style={th}>Level</th>
                <th style={th}>Requested by</th>
                <th style={th}>Requested</th>
                <th style={th}>Goes live</th>
                <th style={th}>Note</th>
                {tab === 'history' ? <th style={th}>Outcome</th> : <th style={th} />}
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.promotionRequestId} data-testid="approval-row"
                  aria-current={r.promotionRequestId === focusId ? 'true' : undefined}
                  style={r.promotionRequestId === focusId ? { background: colors.primaryLight } : undefined}>
                  <td style={{ ...td, fontWeight: 500 }}>
                    <Link to={`/app/templates/${r.templateId}`} style={{ color: colors.primary }}>{r.templateName}</Link>
                  </td>
                  <td style={td}>v{r.versionNumber}</td>
                  <td style={td}>
                    <Badge color={colors.textSecondary} bg={colors.background} border={colors.border}>{APPROVAL_LEVEL_NAME[r.approvalLevel]}</Badge>
                  </td>
                  <td style={td}>{r.requestedByEmail ?? '—'}</td>
                  <td style={{ ...td, whiteSpace: 'nowrap', color: colors.textSecondary }}>{fmt(r.requestedAt)}</td>
                  <td style={{ ...td, whiteSpace: 'nowrap', color: colors.textSecondary }}>{r.promoteAt ? fmt(r.promoteAt) : 'On approval'}</td>
                  <td style={{ ...td, color: colors.textSecondary }}>{r.changeNote || '—'}</td>
                  {tab === 'history' ? (
                    <td style={td}>
                      <Badge {...STATUS_STYLE[r.status]}>{STATUS_LABEL[r.status]}</Badge>
                      {r.decidedAt && <div style={{ fontSize: fontSize.xs, color: colors.textMuted, marginTop: 2 }}>{fmt(r.decidedAt)}</div>}
                      {r.decisionNote && <div data-testid="decision-note" style={{ fontSize: fontSize.xs, color: colors.textSecondary, marginTop: 2 }}>{r.decisionNote}</div>}
                    </td>
                  ) : (
                    <td style={{ ...td, textAlign: 'right' }}>{actions(r)}</td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {modal}
    </div>
  )
}
